import { useEffect, lazy, createElement, Suspense } from 'react'
import type { ComponentType, LazyExoticComponent } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/stores/auth'
import { useLocale } from '@/stores/locale'
import { resolveInitialLocale } from '@/i18n/config'
import { Layout } from '@/components/Layout'
import { RouteErrorBoundary } from '@/components/ErrorBoundary'
import { ROUTE_MANIFEST } from '@/lib/nav'
import { isPublicPath, urlLocale } from '@/lib/publicLocale'
import { reportPageView } from '@/lib/report'
import type { NavPath } from '@/lib/nav'

// 15+ top-level routes are lazy-loaded so initial download is only shell + current route.
// Vendor deps are split via vite manualChunks (see vite.config.ts).
const Landing = lazy(() => import('@/pages/Landing').then((m) => ({ default: m.Landing })))
const Login = lazy(() => import('@/pages/Login').then((m) => ({ default: m.Login })))
const Register = lazy(() => import('@/pages/Register').then((m) => ({ default: m.Register })))
const PrivacyPage = lazy(() => import('@/pages/legal').then((m) => ({ default: m.PrivacyPage })))
const TermsPage = lazy(() => import('@/pages/legal').then((m) => ({ default: m.TermsPage })))
const Docs = lazy(() => import('@/pages/Docs').then((m) => ({ default: m.Docs })))
const Calculator = lazy(() => import('@/pages/Calculator').then((m) => ({ default: m.Calculator })))
const Dashboard = lazy(() => import('@/pages/Dashboard').then((m) => ({ default: m.Dashboard })))
const Models = lazy(() => import('@/pages/Models').then((m) => ({ default: m.Models })))
const ModelDetail = lazy(() => import('@/pages/ModelDetail').then((m) => ({ default: m.ModelDetail })))
const Deployments = lazy(() => import('@/pages/Deployments').then((m) => ({ default: m.Deployments })))
const DeploymentDetail = lazy(
  () => import('@/pages/DeploymentDetail').then((m) => ({ default: m.DeploymentDetail })),
)
const Playground = lazy(() => import('@/pages/Playground').then((m) => ({ default: m.Playground })))
const Datasets = lazy(() => import('@/pages/Datasets').then((m) => ({ default: m.Datasets })))
const Training = lazy(() => import('@/pages/Training').then((m) => ({ default: m.Training })))
const TrainingDetail = lazy(
  () => import('@/pages/TrainingDetail').then((m) => ({ default: m.TrainingDetail })),
)
const Eval = lazy(() => import('@/pages/Eval').then((m) => ({ default: m.Eval })))
const EvalDetail = lazy(() => import('@/pages/EvalDetail').then((m) => ({ default: m.EvalDetail })))
const ApiKeys = lazy(() => import('@/pages/ApiKeys').then((m) => ({ default: m.ApiKeys })))
const Billing = lazy(() => import('@/pages/Billing').then((m) => ({ default: m.Billing })))
const Contracts = lazy(() => import('@/pages/Contracts').then((m) => ({ default: m.Contracts })))
const Notifications = lazy(() => import('@/pages/Notifications').then((m) => ({ default: m.Notifications })))
const Observability = lazy(() => import('@/pages/Observability').then((m) => ({ default: m.Observability })))
const Audit = lazy(() => import('@/pages/Audit').then((m) => ({ default: m.Audit })))
const OrgMembers = lazy(() => import('@/pages/OrgMembers').then((m) => ({ default: m.OrgMembers })))
const AdminAnnouncements = lazy(
  () => import('@/pages/AdminAnnouncements').then((m) => ({ default: m.AdminAnnouncements })),
)
const Projects = lazy(() => import('@/pages/Projects').then((m) => ({ default: m.Projects })))
const ProjectDetail = lazy(() => import('@/pages/ProjectDetail').then((m) => ({ default: m.ProjectDetail })))
const NotFound = lazy(() => import('@/pages/NotFound').then((m) => ({ default: m.NotFound })))

// Component wiring for manifest index routes — Record<NavPath> makes tsc fail if a
// manifest entry has no page (or a stale key survives a rename). Detail routes carry
// no nav metadata, so they stay handwritten below.
const PAGES: Record<NavPath, LazyExoticComponent<ComponentType>> = {
  '/dashboard': Dashboard,
  '/datasets': Datasets,
  '/training': Training,
  '/eval': Eval,
  '/deployments': Deployments,
  '/playground': Playground,
  '/models': Models,
  '/api-keys': ApiKeys,
  '/billing': Billing,
  '/notifications': Notifications,
  '/contracts': Contracts,
  '/projects': Projects,
  '/observability': Observability,
  '/audit': Audit,
  '/members': OrgMembers,
}

/**
 * 未认证访问统一回 /login 并携带原路径（state.from + ?return= 双通道），
 * 登录成功后由 Login 读取并跳回，避免会话失效把用户丢回营销首页丢失上下文。
 */
function Protected({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation()
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading)
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-enterprise-muted">
        {t('common.loading')}
      </div>
    )
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  return <>{children}</>
}

function AuthGate({ children }: { children: React.ReactNode }) {
  const { refresh } = useAuth()
  const location = useLocation()
  useEffect(() => {
    if (isPublicPath(location.pathname)) return
    void refresh()
  }, [refresh, location.pathname])
  return <>{children}</>
}

/**
 * 公开页语言由 URL 决定（/en/* 强制 en，其余交给偏好 — 语义见 src/lib/publicLocale.ts，
 * 这是 hreflang / og:locale:alternate 声明成立的前提），控制台跟随用户偏好。
 * applyLocale 不落盘：路过 /en/ 深链不改写 localStorage 偏好，偏好只在用户显式
 * 切换（LanguageSwitch → setLocale）时写入。
 */
function LocaleGate() {
  const location = useLocation()
  const applyLocale = useLocale((s) => s.applyLocale)
  useEffect(() => {
    applyLocale(urlLocale(location.pathname) ?? resolveInitialLocale())
  }, [applyLocale, location.pathname])
  return null
}

/**
 * 控制台路由的页面浏览信标 —— 每次路由切换发一条 `page_view`（gh #248）。
 * 客户端导航不产生任何 HTTP 请求，没有它后端只知道控制台报了多少错，
 * 不知道展示了多少页。
 *
 * 挂在 <Protected> 里：公开页（/、/login、/register、/privacy、/terms、/docs、
 * /calculator 及其 /en/* 变体）在 Protected 之外，天然不产生 page_view ——
 * 公开面那一侧走的是 reportFunnelStep 那套匿名漏斗。
 *
 * 只发事件、不渲染任何东西；页面身份（orgId / 用户 id / query）一个都不带 ——
 * 漏斗一直是匿名的，页面浏览同样匿名。事件去重与预算都在 reportPageView 里。
 */
function PageViewBeacon() {
  const { pathname } = useLocation()
  useEffect(() => {
    reportPageView(pathname)
  }, [pathname])
  return null
}

/**
 * Explicit loading fallback — avoids white flash / layout shift.
 * Fixed min-height keeps Layout chrome stable while chunks load.
 * Chunk load failures hit the per-route RouteErrorBoundary first (see withSuspense);
 * the global ErrorBoundary (main.tsx) stays as last resort for Layout-level crashes.
 * Tailwind arbitrary: keep max(40vh,200px) without space (space requires max(40vh,_200px) or purge drops it).
 */
function RouteFallback() {
  const { t } = useTranslation()
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="flex min-h-[max(40vh,200px)] items-center justify-center p-8"
    >
      <div className="flex flex-col items-center gap-3 text-sm text-enterprise-muted">
        <div
          className="h-8 w-8 motion-safe:animate-spin motion-reduce:animate-none rounded-full border-2 border-enterprise-border border-t-enterprise-indigo"
          aria-hidden="true"
        />
        <span>{t('common.loading')}</span>
      </div>
    </div>
  )
}

// Boundary sits OUTSIDE Suspense so lazy-chunk load failures (thrown during the
// suspended render, not a catchable promise) are also caught per-route.
function withSuspense(node: React.ReactNode) {
  return (
    <RouteErrorBoundary>
      <Suspense fallback={<RouteFallback />}>{node}</Suspense>
    </RouteErrorBoundary>
  )
}

export function App() {
  return (
    <BrowserRouter>
      <LocaleGate />
      <AuthGate>
        <Routes>
          <Route path="/" element={withSuspense(<Landing />)} />
          <Route path="/login" element={withSuspense(<Login />)} />
          <Route path="/register" element={withSuspense(<Register />)} />
          <Route path="/privacy" element={withSuspense(<PrivacyPage />)} />
          <Route path="/terms" element={withSuspense(<TermsPage />)} />
          <Route path="/docs" element={withSuspense(<Docs />)} />
          <Route path="/calculator" element={withSuspense(<Calculator />)} />
          {/* 英文公开页 — 与 zh 公开页同组件，语言由 LocaleGate 按 URL 强制；
              文档头（og/hreflang）来自第二 HTML 入口 en/index.html */}
          <Route path="/en" element={withSuspense(<Landing />)} />
          <Route path="/en/login" element={withSuspense(<Login />)} />
          <Route path="/en/register" element={withSuspense(<Register />)} />
          <Route path="/en/privacy" element={withSuspense(<PrivacyPage />)} />
          <Route path="/en/terms" element={withSuspense(<TermsPage />)} />
          <Route path="/en/docs" element={withSuspense(<Docs />)} />
          <Route path="/en/calculator" element={withSuspense(<Calculator />)} />
          <Route
            element={
              <Protected>
                {/* 页面浏览信标挂在 Protected 内 —— 公开页在它之外，不产生 page_view */}
                <PageViewBeacon />
                <Layout />
              </Protected>
            }
          >
            {/* index 路由由 nav.ts ROUTE_MANIFEST 派生 — 加页面只改 manifest + PAGES */}
            {ROUTE_MANIFEST.map((e) => (
              <Route key={e.to} path={e.to.slice(1)} element={withSuspense(createElement(PAGES[e.to]))} />
            ))}
            <Route path="training/:id" element={withSuspense(<TrainingDetail />)} />
            <Route path="eval/:runId" element={withSuspense(<EvalDetail />)} />
            <Route path="deployments/:id" element={withSuspense(<DeploymentDetail />)} />
            <Route path="models/:modelId" element={withSuspense(<ModelDetail />)} />
            <Route path="projects/:projectId" element={withSuspense(<ProjectDetail />)} />
            {/* 运营面不进 ROUTE_MANIFEST（不出现在侧栏/移动导航）——URL 直达，
                非 operator 会话由后端 admin-guard 的 403 在页内显式呈现。 */}
            <Route path="admin/announcements" element={withSuspense(<AdminAnnouncements />)} />
            {/* 404 留在控制台里（带侧栏/面包屑上下文），未认证则由 Protected 引去 /login。
                不再全局踢回 `/` — 那会把已登录用户丢到营销首页丢光上下文。 */}
            <Route path="*" element={withSuspense(<NotFound />)} />
          </Route>
        </Routes>
      </AuthGate>
    </BrowserRouter>
  )
}
